/**
 * @lastAudited 2025-01-19
 * @auditStatus PASS
 * @auditNotes Pre-publish checklist passed. No issues found.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useVehicles } from '../../hooks/useVehicles';
import VehicleCard from '../inventory/components/VehicleCard';
import { useSEOHead } from '../../hooks/useSEOHead';
import Breadcrumb from '../../components/feature/Breadcrumb';
import { injectJsonLd, removeJsonLd } from '../../lib/schemaBuilder';

export default function UsedCarsIlwacoPage() {
  useSEOHead({
    title: 'Used Cars Ilwaco WA — Honest Pricing, Free Delivery | Rockhound Auto',
    description: 'Buy quality used cars near Ilwaco, Washington from Rockhound Auto in Warrenton, OR. Free delivery to Ilwaco. Flat $250 doc fee, inspected inventory, all cross-state paperwork handled.',
    keywords: 'used cars ilwaco washington, cars for sale ilwaco wa, buy car oregon from washington, ilwaco fishing trucks, long beach peninsula vehicles, cape disappointment auto',
    canonicalUrl: 'https://rockhoundauto.com/used-cars-ilwaco-washington',
    geoPosition: '46.3087;-124.0432',
    geoRegion: 'US-WA',
    geoPlacename: 'Ilwaco, Washington',
  });

  const { vehicles, loading } = useVehicles();
  const [selectedType, setSelectedType] = useState<string>('all');

  const filteredVehicles = vehicles
    .filter(v => v.status === 'available')
    .filter(v => selectedType === 'all' || v.type === selectedType)
    .slice(0, 6);

  const faqs = [
    {
      question: "Why do Ilwaco residents buy cars from Oregon dealerships?",
      answer: "Ilwaco residents shop at Rockhound Auto in Warrenton for our transparent pricing, flat $250 doc fee, and no-pressure process. We're just 10 minutes away across the Astoria-Megler Bridge and offer free delivery to Ilwaco. Note: Washington residents still pay Washington state use tax when registering at home — where you register is where the taxes apply."
    },
    {
      question: "What types of vehicles are most popular in Ilwaco Washington?",
      answer: "Ilwaco is a working fishing town, so work trucks like Toyota Tacoma, Ford F-150, and Chevrolet Silverado dominate for towing boats and hauling gear. SUVs with AWD like Subaru Outback are popular for coastal weather. Towing capacity and rust resistance matter most for fishing operations."
    },
    {
      question: "Do you deliver used cars to Ilwaco for free?",
      answer: "Yes, we offer free delivery anywhere in Ilwaco and the Long Beach Peninsula. We're just 10 minutes away in Warrenton, Oregon. We bring the vehicle to your Ilwaco home or port for a test drive. If you love it, we complete paperwork on-site including Washington registration."
    },
    {
      question: "How does cross-state car buying work from Oregon to Washington?",
      answer: "We handle everything. You browse inventory online, schedule a test drive in Ilwaco, and we bring the vehicle to you. We complete Washington title and registration paperwork, and you'll pay Washington use tax when registering at your local DOL. You drive home on a temporary permit and receive Washington plates by mail."
    },
    {
      question: "Can I get financing for a car purchased in Oregon?",
      answer: "Yes, we work with credit unions and lenders on both sides of the Columbia River. We help Ilwaco residents secure competitive financing for Oregon purchases. Cross-state financing is common and straightforward. We handle all paperwork and coordinate with Washington lenders."
    },
    {
      question: "Are your used cars inspected for coastal weather and fishing work?",
      answer: "Every vehicle is inspected for coastal durability including rust protection, suspension, and towing capacity. We understand salt air, boat ramps, and fishing work demands. All vehicles are detailed and ready for Ilwaco's maritime environment before delivery."
    },
    {
      question: "How long does it take to buy a car from Rockhound Auto?",
      answer: "Most Ilwaco customers complete the entire purchase in 30 to 60 minutes, including cross-state paperwork and financing. You can also take days to decide—we work on your timeline. No dealership pressure tactics or wasted weekends at sales lots."
    },
    {
      question: "What's the best truck for Ilwaco fishing and boat towing?",
      answer: "For Ilwaco fishing operations, we recommend Toyota Tacoma or Ford F-150 with 4WD and towing capacity. These offer reliability, rust resistance, and durability for towing boats, hauling gear, and accessing boat ramps. They hold value incredibly well and have affordable parts."
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  useEffect(() => {
    injectJsonLd('ilwaco-faq-schema', faqSchema);

    injectJsonLd('schema-ilwaco-breadcrumb', {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: 'https://rockhoundauto.com/'
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Used Cars',
          item: 'https://rockhoundauto.com/inventory'
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: 'Ilwaco Washington',
          item: 'https://rockhoundauto.com/used-cars-ilwaco-washington'
        }
      ]
    });

    return () => {
      removeJsonLd('ilwaco-faq-schema');
      removeJsonLd('schema-ilwaco-breadcrumb');
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Breadcrumb items={[{ label: 'Home', path: '/' }, { label: 'Used Cars Ilwaco Washington' }]} />

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://public.readdy.ai/ai/img_res/bae995f98964a6a305023dbd4d118352.jpg')] bg-cover bg-center opacity-20"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Used Cars for Ilwaco — Honest Pricing, Free Delivery, No Dealer Games
            </h1>
            <p className="text-xl sm:text-2xl text-slate-200 mb-8 leading-relaxed">
              We're 20 minutes south in Warrenton, Oregon. Every vehicle is inspected for coastal durability — wind, rain, and salt air. Flat $250 doc fee, free delivery to Ilwaco, and zero pressure tactics.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/inventory"
                className="inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-car-line text-xl"></i>
                Browse Inventory
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all border border-white/20 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line text-xl"></i>
                Get in Touch
              </Link>
            </div>
            
            <div className="mt-12 grid grid-cols-3 gap-6">
              <div>
                <div className="text-3xl font-bold text-teal-400 mb-1">$250</div>
                <div className="text-sm text-slate-300">Flat Doc Fee</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-teal-400 mb-1">20 min</div>
                <div className="text-sm text-slate-300">From Ilwaco Harbor</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-teal-400 mb-1">Free</div>
                <div className="text-sm text-slate-300">Delivery to Your Door</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Ilwaco Buyers Choose Us */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Why Ilwaco Buyers Choose Rockhound Auto
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Transparent pricing, a flat $250 doc fee, and vehicles actually inspected for the weather you'll be driving in.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                  <i className="ri-price-tag-3-line text-2xl text-teal-600"></i>
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Flat $250 Doc Fee — No Surprises</h3>
                <p className="text-slate-600 leading-relaxed">
                  Oregon caps doc fees at $150 by law, and we charge a flat $250 all-in — far below the $300–$500+ many Washington dealerships tack on. The price you see is the price you pay. We handle all cross-state paperwork so you can register in Washington hassle-free.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                  <i className="ri-shield-check-line text-2xl text-teal-600"></i>
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Built for Cape Disappointment Weather</h3>
                <p className="text-slate-600 leading-relaxed">
                  Every vehicle we deliver to the <Link to="/service-areas/ilwaco" className="text-teal-700 font-semibold underline underline-offset-2 hover:text-teal-900 transition-colors">Ilwaco service area</Link> is inspected for wind, rain, and salt air durability. We check seals, undercarriage, and electrical systems. If it can't handle a drive to the lighthouse in February, it doesn't make our lot.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                  <i className="ri-truck-line text-2xl text-teal-600"></i>
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Free Delivery to Ilwaco</h3>
                <p className="text-slate-600 leading-relaxed">
                  Live in Ilwaco, Long Beach, or anywhere on the peninsula? We'll bring the car to you anywhere in the <Link to="/service-areas/ilwaco" className="text-teal-700 font-semibold underline underline-offset-2 hover:text-teal-900 transition-colors">Ilwaco service area</Link>. No need to coordinate rides or take time off work. We handle the logistics so you don't have to.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                  <i className="ri-user-heart-line text-2xl text-teal-600"></i>
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Locals Helping Locals</h3>
                <p className="text-slate-600 leading-relaxed">
                  We're not some Seattle dealer trying to dump inventory on peninsula buyers. We live on the coast. We know the roads, the weather, and what actually holds up. If we sell you junk, we'll see you at the Port. Reputation matters.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-12 text-slate-900 text-center">
              Frequently Asked Questions About Buying Used Cars in Ilwaco Washington
            </h2>
            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                  <h3 className="text-xl font-bold text-slate-900 mb-3">
                    {faq.question}
                  </h3>
                  <p className="text-slate-700 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Available Inventory */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4 text-slate-900">
              Used Cars Available for Ilwaco Delivery
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              All vehicles are inspected, detailed, and ready for free delivery to Ilwaco, Washington.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-3 mb-12">
            <button
              onClick={() => setSelectedType('all')}
              className={`px-6 py-2 rounded-full font-semibold transition-colors whitespace-nowrap ${
                selectedType === 'all'
                  ? 'bg-teal-600 text-white'
                  : 'bg-white text-slate-700 hover:bg-slate-100'
              }`}
            >
              All Vehicles
            </button>
            <button
              onClick={() => setSelectedType('Truck')}
              className={`px-6 py-2 rounded-full font-semibold transition-colors whitespace-nowrap ${
                selectedType === 'Truck'
                  ? 'bg-teal-600 text-white'
                  : 'bg-white text-slate-700 hover:bg-slate-100'
              }`}
            >
              Trucks
            </button>
            <button
              onClick={() => setSelectedType('SUV')}
              className={`px-6 py-2 rounded-full font-semibold transition-colors whitespace-nowrap ${
                selectedType === 'SUV'
                  ? 'bg-teal-600 text-white'
                  : 'bg-white text-slate-700 hover:bg-slate-100'
              }`}
            >
              SUVs
            </button>
            <button
              onClick={() => setSelectedType('Sedan')}
              className={`px-6 py-2 rounded-full font-semibold transition-colors whitespace-nowrap ${
                selectedType === 'Sedan'
                  ? 'bg-teal-600 text-white'
                  : 'bg-white text-slate-700 hover:bg-slate-100'
              }`}
            >
              Sedans
            </button>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
            </div>
          ) : filteredVehicles.length > 0 ? (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
                {filteredVehicles.map((vehicle) => (
                  <VehicleCard key={vehicle.id} vehicle={vehicle} />
                ))}
              </div>
              <div className="text-center">
                <Link
                  to="/inventory"
                  className="inline-block bg-teal-600 hover:bg-teal-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors whitespace-nowrap"
                >
                  View All Available Inventory
                </Link>
              </div>
            </>
          ) : (
            <div className="text-center py-12 bg-white rounded-xl">
              <i className="ri-car-line text-6xl text-slate-300 mb-4"></i>
              <p className="text-xl text-slate-600 mb-6">
                No vehicles currently match your filter. Check back soon or view all inventory.
              </p>
              <Link
                to="/inventory"
                className="inline-block bg-teal-600 hover:bg-teal-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors whitespace-nowrap"
              >
                View All Inventory
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Service Area Cross-Link */}
      <section className="py-10 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-teal-50 border border-teal-200 rounded-xl p-6 flex items-center justify-between gap-4 flex-wrap">
              <div>
                <h4 className="font-bold text-slate-900 mb-1">Ilwaco Service Area Details</h4>
                <p className="text-slate-600 text-sm">
                  See our full Ilwaco service area page — delivery zones, nearby Long Beach Peninsula cities we cover, and why Ilwaco fishing families choose Rockhound Auto over Washington dealerships.
                </p>
              </div>
              <Link
                to="/service-areas/ilwaco"
                className="inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-5 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap text-sm"
              >
                Ilwaco Service Area <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-gradient-to-br from-teal-600 to-cyan-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">
            Ready to Buy a Car from Oregon?
          </h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto opacity-90">
            Browse our inventory online, schedule a free test drive in Ilwaco, and save thousands buying from Oregon. We handle all cross-state paperwork. No dealership pressure, no hidden fees.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/inventory"
              className="bg-white text-teal-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-slate-100 transition-colors whitespace-nowrap"
            >
              View Available Inventory
            </Link>
            <Link
              to="/contact"
              className="bg-teal-700 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-teal-800 transition-colors whitespace-nowrap border-2 border-white/30"
            >
              Schedule Test Drive
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
